import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  OnFarmImplantRequestError,
  type RowVersions,
  useSendOnFarmImplantToFreezing,
} from '@/services/on-farm-implant';

export type SendToFreezingEmbryo = {
  sessionEmbryoId: string;
  embryoName: string | null;
  strawNumber: number | null;
  stage: number | null;
  grade: number | null;
  versions?: RowVersions;
};

type SendToFreezingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: SendToFreezingEmbryo[];
  nameHeader?: string;
  fromQueue?: boolean;
  onSent: () => void;
};

export const SendToFreezingDialog = ({
  open,
  onOpenChange,
  sessionId,
  embryos,
  nameHeader,
  fromQueue = false,
  onSent,
}: SendToFreezingDialogProps) => {
  const { t } = useTranslation();

  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: sendToFreezing, isPending } =
    useSendOnFarmImplantToFreezing();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setProblems([]);
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0) return;

    setProblems([]);

    try {
      const result = await sendToFreezing({
        sessionId,
        input: {
          sessionEmbryoIds: embryos.map((embryo) => embryo.sessionEmbryoId),
          versions: collectVersions(embryos),
        },
      });

      handleOpenChange(false);
      onSent();

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.onFarmFreezing.send.success', {
          count: result.totalRecords,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to send embryos to On-Farm Freezing:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.onFarmFreezing.send.failed'),
      });
    }
  }, [embryos, handleOpenChange, onSent, sendToFreezing, sessionId, t]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.onFarmFreezing.send.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.onFarmFreezing.send.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {nameHeader ?? t('onFarmImplant.onFarmFreezing.embryo')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.onFarmFreezing.straw')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.onFarmFreezing.stage')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.onFarmFreezing.grade')}
                </th>
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => (
                <tr key={embryo.sessionEmbryoId} className="border-t">
                  <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                  <td className="py-1.5">{embryo.strawNumber ?? '—'}</td>
                  <td className="py-1.5">{embryo.stage ?? '—'}</td>
                  <td className="py-1.5">{embryo.grade ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {fromQueue && (
            <p className="text-xs text-amber-700">
              {t('onFarmImplant.onFarmFreezing.send.queueWarning')}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={embryos.length === 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.onFarmFreezing.send.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
